<script setup>
import LegalSummary from 'components/LegalSummary.vue';
import TableOfContents from 'components/TableOfContents.vue';
import { usePageMeta } from 'composables/page-meta';
import { LEGAL_DOCUMENTS_EFFECTIVE_DATE } from 'src/configs/legal';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const tableOfContentsElement = ref();
const tableOfContentsItems = [
    {
        id: 'tldr',
        title: 'TL;DR'
    },
    {
        id: 'definitions',
        title: 'Definitions'
    },
    {
        id: 'collecting-and-using-your-personal-data',
        title: 'Collecting and Using Your Personal Data',
        children: [
            {
                id: 'types-of-data-collected',
                title: 'Types of Data Collected',
                children: [
                    {
                        id: 'personal-data',
                        title: 'Personal Data'
                    },
                    {
                        id: 'profile-data',
                        title: 'Profile Data'
                    },
                    {
                        id: 'profile-link-visits',
                        title: 'Profile Link Visits'
                    },
                    {
                        id: 'information-about-other-people',
                        title: 'Information About Other People'
                    },
                    {
                        id: 'usage-data',
                        title: 'Usage Data'
                    },
                    {
                        id: 'information-from-third-party-social-media-services',
                        title: 'Information from Login Services'
                    },
                    {
                        id: 'cookie-data',
                        title: 'Cookie Data'
                    },
                    {
                        id: 'local-storage-data',
                        title: 'Local Storage Data'
                    }
                ]
            },
            {
                id: 'use-of-your-personal-data',
                title: 'Use of Your Personal Data'
            },
            {
                id: 'xprt-dev-and-xprt-jobs',
                title: 'Xprt Dev and Xprt Jobs'
            },
            {
                id: 'market-figures',
                title: 'Market Information'
            },
            {
                id: 'non-sale-of-your-personal-data',
                title: 'Non-Sale of Your Personal Data'
            },
            {
                id: 'ownership-of-your-personal-data',
                title: 'Ownership of Your Personal Data'
            },
            {
                id: 'security-of-your-personal-data',
                title: 'Security of Your Personal Data'
            },
            {
                id: 'capturing-and-printing-your-personal-data',
                title: 'Capturing and Printing Your Personal Data'
            },
            {
                id: 'retention-of-your-personal-data',
                title: 'Retention of Your Personal Data'
            },
            {
                id: 'deletion-of-your-personal-data',
                title: 'Deletion of Your Personal Data'
            },
            {
                id: 'disclosure-of-your-personal-data',
                title: 'Disclosure of Your Personal Data'
            }
        ]
    },
    {
        id: 'data-storage-and-international-transfers',
        title: 'Data Storage and International Transfers'
    },
    {
        id: 'third-party-services',
        title: 'Third-Party Services',
        children: [
            {
                id: 'google-recaptcha',
                title: 'Google reCAPTCHA'
            },
            {
                id: 'jira-cloud',
                title: 'Jira Cloud'
            },
            {
                id: 'sentry',
                title: 'Sentry'
            },
            {
                id: 'email-delivery',
                title: 'Email Delivery'
            }
        ]
    },
    {
        id: 'minimum-age',
        title: 'Minimum Age'
    },
    {
        id: 'links-to-other-websites',
        title: 'Links to Other Websites'
    },
    {
        id: 'changes-to-this-privacy-policy',
        title: 'Changes to this Privacy Policy'
    },
    {
        id: 'contact-us',
        title: 'Contact Us'
    }
];

const summaryLead = 'A short summary so you do not have to read the whole policy. The full policy below applies if '
    + 'anything here is unclear.';

const summaryCards = [
    {
        title: 'What we collect',
        points: [
            {
                label: 'Account details:',
                text: 'your email address, and your name and photo if you log in with GitHub, GitLab, Google or '
                    + 'LinkedIn.'
            },
            {
                label: 'What you choose to add:',
                text: 'the one market you save in My Market, and anything you put in your optional Career profile, '
                    + 'including a website and the names of other public profiles.'
            },
            {
                label: 'Usage and visits:',
                text: 'the pages you visit, your browser and IP address, and visits to profile links.'
            },
            {
                label: 'Other people\'s details:',
                text: 'the email address of anyone you invite, and the name and email address of anyone you send a '
                    + 'profile link to.'
            },
            {
                label: 'Where it is kept:',
                text: 'mainly in Singapore, with backups and public files such as profile photos in Australia.'
            }
        ]
    },
    {
        title: 'How we use it',
        points: [
            {
                label: 'To run Xprt Dev:',
                text: 'your account, My Market, and your Career profile, shown only as its access setting allows.'
            },
            {
                label: 'With Xprt Jobs:',
                text: 'one account covers both sites. Basic profile details can prefill an application there, and only '
                    + 'for a role that accepts applications on Xprt Jobs. The employer receives that application when '
                    + 'you apply, not your Career profile.'
            },
            {
                label: 'To keep it safe and working:',
                text: 'reCAPTCHA checks for abuse, Sentry reports errors, and Jira holds the support requests you send.'
            },
            {
                label: 'Email:',
                text: 'a welcome message, login codes, security notices, and the invitations and profile links you '
                    + 'choose to send. We do not send marketing email.'
            }
        ]
    },
    {
        title: 'What we do not do',
        points: [
            {
                label: 'No selling:',
                text: 'we do not sell, rent or lease personal data.'
            },
            {
                label: 'No advertising cookies:',
                text: 'no third-party analytics or advertising cookies. Our analytics runs on our own infrastructure.'
            },
            {
                label: 'No personal data in market figures:',
                text: 'the figures come from job listings. Your saved market, profile and activity are never used as '
                    + 'market data.'
            },
            {
                label: 'No stored provider access:',
                text: 'we keep no access to your GitHub, GitLab, Google or LinkedIn account after you log in.'
            }
        ]
    },
    {
        title: 'Your controls',
        points: [
            {
                label: 'Profile access:',
                text: 'make your Career profile public, or share it only through protected links. Public mode needs a '
                    + 'linked GitHub or GitLab account.'
            },
            {
                label: 'Links and invitations:',
                text: 'password-protect, expire or delete profile links, and delete invitations, at any time.'
            },
            {
                label: 'Your data:',
                text: 'update your details and profile from your account pages, or email team@xprt.dev or '
                    + 'team@xprt.jobs to access, correct or delete your data. Closing the account closes it on both '
                    + 'sites.'
            },
            {
                label: 'Concerns:',
                text: 'email team@xprt.dev and we will respond within 30 days. If that does not resolve a privacy '
                    + 'complaint, you can take it to the OAIC.'
            }
        ]
    }
];

onMounted(() => {
    const anchorId = router.currentRoute.value.hash;
    if (anchorId) {
        setTimeout(() => tableOfContentsElement.value.scrollToAnchor(anchorId.replace('#', '')), 0);
    }
});

usePageMeta(buildPageMeta({
    title: 'Privacy Policy',
    description: 'How Xprt Dev collects, uses and protects account, saved market and career-profile data, including '
        + 'privacy controls, retention and third-party services.',
    canonicalUrl: 'https://xprt.dev/legal/privacy'
}));
</script>

<template>
    <main class="privacy-policy-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                        Legal · Last updated and effective {{ LEGAL_DOCUMENTS_EFFECTIVE_DATE }}
                    </p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">Privacy Policy</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-my-none">
                        This Privacy Policy explains how, when and why Xprt Dev collects and uses information about you.
                        By using the service, you agree to that collection and use as this policy describes it.
                    </p>
                </div>
            </div>
        </header>
        <div class="contents-layout container-md q-px-md q-py-xl">
            <aside aria-labelledby="contents-heading" class="contents-rail">
                <p id="contents-heading" class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">On this page</p>
                <nav aria-label="Privacy Policy sections">
                    <TableOfContents ref="tableOfContentsElement" :items="tableOfContentsItems"/>
                </nav>
            </aside>
            <article class="legal-copy contents-copy">
                <LegalSummary :cards="summaryCards" :lead="summaryLead"/>
                <a id="definitions" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Definitions</h2>
                <p>
                    For the purposes of this Privacy Policy:
                </p>
                <ul>
                    <li>
                        <strong>"We", "our" or "us"</strong> refers to 0x234 Pty Ltd, an Australian proprietary limited company.
                    </li>
                    <li>
                        <strong>"You" or "your"</strong> refers to the person or entity accessing or using the service.
                    </li>
                    <li>
                        <strong>"Service" or "platform"</strong> refers to Xprt Dev, our website at xprt.dev, and the
                        content, application and infrastructure that power it.
                    </li>
                    <li>
                        <strong>"Xprt Jobs"</strong> refers to our job search and hiring service at xprt.jobs, which
                        shares its account system with Xprt Dev.
                    </li>
                    <li>
                        <strong>"Account"</strong> refers to a unique account created for you to access the service or parts of the service.
                    </li>
                    <li>
                        <strong>"Career profile"</strong> refers to the optional professional profile you can build,
                        publish and share through the service.
                    </li>
                    <li>
                        <strong>"My Market"</strong> refers to the one market, an ICT specialisation and an Australian
                        location, that an account can save to return to.
                    </li>
                    <li>
                        <strong>"Device"</strong> refers to any electronic device that can access the service, including
                        a computer, a mobile phone or a tablet.
                    </li>
                    <li>
                        <strong>"Personal data"</strong> refers to any information that relates to an identified or identifiable individual.
                    </li>
                    <li>
                        <strong>"Login service"</strong> refers to GitHub, GitLab, Google or LinkedIn, which you can use
                        to log in or to create an account.
                    </li>
                    <li>
                        <strong>"Usage data"</strong> refers to data collected automatically, either generated by the use of the service or from the service infrastructure itself (for
                        example, the duration of a page visit).
                    </li>
                    <li>
                        <strong>"User"</strong> refers to any person or entity accessing the service, with or without an account.
                    </li>
                </ul>
                <a id="collecting-and-using-your-personal-data" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Collecting and Using Your Personal Data</h2>
                <a id="types-of-data-collected" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Types of Data Collected</h3>
                <a id="personal-data" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Personal Data</h4>
                <p>
                    When creating an account, we ask for or receive personal information that identifies you so we can manage
                    the account and the features you choose to use. The account information we may receive is:
                </p>
                <ul>
                    <li>Email address</li>
                    <li>
                        A display name, commonly a first name, a last name, or both, when a login service supplies it or
                        you add it to a Career profile
                    </li>
                </ul>
                <p>
                    When you log in by email, we send a one-time code to your email address to confirm that it is yours.
                </p>
                <p>
                    Saving a market in My Market is optional. If you use it, we store your selected Australian location
                    and ICT classification preference, being Australia or one state or territory and one ICT
                    specialisation, so the service can return you to that market view.
                </p>
                <p>In addition, based on your usage, we automatically collect your:</p>
                <ul>
                    <li>Browser type</li>
                    <li>Device IP address</li>
                    <li>Visited pages of the service</li>
                </ul>
                <a id="profile-data" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Profile Data</h4>
                <p>
                    Email registration on Xprt Dev creates an account without creating a Career profile. Setting the
                    account up on Xprt Jobs asks for your name and location and stores them on the shared profile. If
                    you register with a login service, the service creates a basic Career profile using the display
                    name that service supplies. Completing, publishing and sharing a Career profile on Xprt Dev are
                    optional, and it stays private until you choose another access mode.
                </p>
                <p>
                    A profile may include a display name, a headline, an introduction, a website address, and the
                    usernames of other public profiles you choose to list. It may also include your work and education
                    history, certifications, projects and code repositories, which can name a current or past employer.
                    You choose how specific the location is:
                </p>
                <ul>
                    <li>Country</li>
                    <li>State and country</li>
                    <li>City, state and country</li>
                </ul>
                <p>
                    When you choose that location, your browser may ask for your device's location so we can suggest
                    nearby places. We send the coordinates only with that search, and never save them to your account or
                    profile. We store the place you select.
                </p>
                <p>
                    Where a login service supplies a profile photo, the service may import it, and you can upload a
                    photo of your own. Profile photos, and images you add to a project, are stored in our public file
                    storage in Australia, because they are shown wherever your profile appears. Share only what you have
                    the right to share, particularly anything covered by a confidentiality obligation.
                </p>
                <p>
                    If you link a GitHub or GitLab account and turn on public contributions, we read the contribution
                    counts that service already shows publicly for your username, such as how many contributions you
                    made each day. We do not request the contents of private repositories. We cache those counts for six
                    hours, and we use our own access to the service to read them, not ongoing access to your account.
                </p>
                <p>
                    You choose who can see a Career profile. It can be public or available only through a protected
                    profile link. Public mode requires a linked GitHub or GitLab account. A profile link can have its
                    own password and expiry, and you can delete it at any time. You can update the profile, its access
                    setting and its links from your account.
                </p>
                <a id="profile-link-visits" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Profile Link Visits</h4>
                <p>
                    When someone opens a profile link, we record the visit: the date and time, the page, the visitor's
                    IP address and browser details, a pseudonymous visitor identifier held in the session, and their
                    account if they are logged in. Profile owners see how many visits and visitors their links receive,
                    never who the visitors are. We also use these records to protect the service from abuse.
                </p>
                <p>
                    After 90 days, we remove the visitor's IP address and browser details from the visit, and keep only
                    what we need to count visits.
                </p>
                <a id="information-about-other-people" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Information About Other People</h4>
                <p>
                    Some features ask for another person's details. When you send an invitation by email, we store the
                    recipient's email address with the invitation, and count visits to the invitation and the accounts
                    created through it. When you email a profile link, we use the recipient's name and email address,
                    together with your subject and message, to send it. Only send invitations and profile links to
                    people who would expect to hear from you.
                </p>
                <a id="usage-data" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Usage Data</h4>
                <p>
                    Usage data is collected automatically when you use the service. It includes your device's IP
                    address, browser type and version, the pages you visit, the time and date of the visit, the time
                    spent on those pages, and other diagnostic data. Your browser sends the same kind of information
                    when you use a mobile device.
                </p>
                <p>
                    We use Umami, a privacy-first analytics system hosted on our own infrastructure, to understand how
                    the service is used and to improve it. Analytics are collected in an anonymised and aggregated form
                    where possible, and are not used to identify you or to advertise to you. The product events we send
                    are limited to coarse facts such as which market page was opened. They do not include your name,
                    email address or profile.
                </p>
                <p>
                    IP addresses may be recorded in server logs for security and operational purposes. They are not
                    stored as part of the analytics dataset.
                </p>
                <a id="information-from-third-party-social-media-services" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Information from Login Services</h4>
                <p>
                    You can create an account and log in through these login services:
                </p>
                <ul>
                    <li><strong>GitHub:</strong> <a href="https://github.com" target="_blank">github.com</a></li>
                    <li><strong>GitLab:</strong> <a href="https://gitlab.com" target="_blank">gitlab.com</a></li>
                    <li><strong>Google:</strong> <a href="https://accounts.google.com" target="_blank">accounts.google.com</a></li>
                    <li><strong>LinkedIn:</strong> <a href="https://www.linkedin.com" target="_blank">linkedin.com</a></li>
                </ul>
                <p>
                    When you register or log in through one of these services, it shares details from your account there
                    with us. What it shares depends on the service, and can include your name, email address, username,
                    profile photo and other public profile details, such as a bio or location. We keep your email
                    address, and your username and account identifier on that service. When you register, we also keep
                    your name and profile photo for your Career profile. We use anything else it shares only to log you
                    in, and do not store it. For GitHub and GitLab, we also use the username to show public contribution
                    counts when you have turned that on. We do not keep access to these services after you have logged
                    in.
                </p>
                <a id="cookie-data" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Cookie Data</h4>
                <p>
                    A cookie is a small piece of data stored on your device, either by us or by a service we rely on. A
                    session cookie lasts for the current visit and is usually removed when you close the browser. A
                    persistent cookie stays until it expires or you delete it.
                </p>
                <p>
                    Xprt Dev uses two first-party cookies. The login cookie keeps you logged in. The visitor cookie
                    holds a pseudonymous visitor identifier so we can count unique visits. That identifier is not used
                    to identify you. If you block or delete these cookies, you will be logged out, and parts of the
                    service may not work.
                </p>
                <p>
                    We do not use third-party cookies for analytics or advertising. Google reCAPTCHA, described below,
                    sets its own cookie.
                </p>
                <p>
                    For further information on cookies used, please refer to the table below:
                </p>
                <div class="table-scrollable q-mb-lg">
                    <table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Purpose</th>
                            <th>Domain</th>
                            <th>Max. Retention</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>_xid</td>
                            <td>Used for your session, including keeping you logged in.</td>
                            <td>xprt.dev</td>
                            <td>30 days</td>
                        </tr>
                        <tr>
                            <td>_xvt</td>
                            <td>Holds the pseudonymous visitor identifier that counts unique visits.</td>
                            <td>xprt.dev</td>
                            <td>180 days</td>
                        </tr>
                        <tr>
                            <td>_grecaptcha</td>
                            <td>Used for providing a risk analysis of the user.</td>
                            <td>google.com</td>
                            <td>6 months</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <p>
                    If the device is not your own, including a public or work device, use it only with the owner's
                    permission. By continuing to use the service, you confirm that you are allowed to accept cookies on
                    that device.
                </p>
                <a id="local-storage-data" class="no-hover-decoration"></a>
                <h4 class="text-subtitle1 text-weight-bold">Local Storage Data</h4>
                <p>
                    Like cookies, the service uses your browser's local storage so a few preferences can stay on your
                    device between visits. This data includes:
                </p>
                <ul>
                    <li>Settings for your personalised experience, such as your theme.</li>
                    <li>
                        An invitation code, if you arrive through an invitation link, so the invitation can be credited
                        when you create an account.
                    </li>
                    <li>Whether you have dismissed a notice, such as the one announcing an update to this policy.</li>
                </ul>
                <p>
                    If the device is not your own, including a public or work device, log out before you leave, and
                    clear the browser data for the service.
                </p>
                <a id="use-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Use of Your Personal Data</h3>
                <p>
                    We may use your personal data for the following purposes:
                </p>
                <ul>
                    <li>
                        <strong>Account management:</strong> We use your personal data to run your account, My Market
                        and your Career profile.
                    </li>
                    <li>
                        <strong>Communication:</strong> We email you about your account and the features you use. That
                        includes a welcome message, one-time login codes, security notices, and the invitations and
                        profile links you ask us to send.
                    </li>
                    <li>
                        <strong>Marketing:</strong> We do not send marketing email. If that ever changes, we will ask
                        you first.
                    </li>
                    <li>
                        <strong>Customer support:</strong> When you report a bug, request a feature or report a profile,
                        we use your personal data to handle that request.
                    </li>
                    <li>
                        <strong>Analytics and improvements:</strong> We may analyse usage data to understand how the
                        service is used and to improve it. We do not use it to advertise to you.
                    </li>
                    <li>
                        <strong>Safety and abuse prevention:</strong> We use usage and visit records, and Google
                        reCAPTCHA, to detect and prevent spam, scraping and other abuse of the service.
                    </li>
                </ul>
                <a id="xprt-dev-and-xprt-jobs" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Xprt Dev and Xprt Jobs</h3>
                <p>
                    Xprt Dev and Xprt Jobs are both operated by us and share one account system. If you log in to Xprt
                    Jobs with the same email address or login service, you use the same account and the same Career
                    profile. Basic details from that profile, such as your name, location and introduction, may prefill
                    an application on Xprt Jobs, and only for a role that accepts applications there. They are sent to
                    an employer only when you submit that application. The employer receives that application, not your
                    Career profile. Xprt Jobs may use the location on your profile to prefer jobs near that place. A
                    phone number or resume saved while using Xprt Jobs stays on the same account. Xprt Dev does not
                    show or publish either of them. An application you make on an employer's or recruiter's own site is
                    separate, and Xprt Dev does not receive it.
                    The <a href="https://xprt.jobs/legal/privacy" target="_blank">Xprt Jobs Privacy Policy</a> explains
                    what Xprt Jobs collects for job search, applications and payments.
                </p>
                <p>
                    Links on the market pages that open Xprt Jobs carry only the market being viewed, never your account
                    or profile.
                </p>
                <a id="market-figures" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Market Information</h3>
                <p>
                    The market pages publish figures calculated from job listings on Xprt Jobs that advertise pay. The
                    figures include counts of active roles, advertised salaries, work arrangements, employment types,
                    the organisations advertising them, and, on the overview, movement over 30 days. They contain no
                    information about Xprt Dev users. Your saved market, Career profile and activity are never
                    published, sold or used as market data.
                </p>
                <a id="non-sale-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Non-Sale of Your Personal Data</h3>
                <p>
                    We do not sell, rent or lease your personal data to anyone, for marketing or for any other purpose.
                    We use it to run the service and to improve it.
                </p>
                <a id="ownership-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Ownership of Your Personal Data</h3>
                <p>
                    Your personal data belongs to you. We do not harvest what you add to your profile or sell it, and we
                    show it only as your settings allow. When a login service creates a basic Career profile, you decide
                    whether to complete, publish or share it, and what to add. Email registration on Xprt Dev does not
                    create a Career profile. The controls below are how you decide who can see one.
                </p>
                <p>
                    You can limit who sees a profile. Make it public or keep it available only through a profile link.
                    A link can be password-protected and can expire, and the profile address can be a public alias you
                    choose. Public mode requires a linked GitHub or GitLab account.
                </p>
                <p>
                    A change or deletion on the service, including removal of a public profile, may not show in search
                    results straight away. A search engine can keep its own copy, and we cannot make it delete that
                    copy. If outdated information remains, ask the search engine to remove it.
                </p>
                <a id="security-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Security of Your Personal Data</h3>
                <p>
                    We take reasonable steps to protect your personal data from misuse, loss, and unauthorised access,
                    change or disclosure. No way of sending or storing data over the internet is completely secure, so
                    we cannot guarantee that it is. Data sent between your browser and the service is encrypted in
                    transit.
                </p>
                <p>
                    If a data breach is likely to cause you serious harm, we will tell you, and the Office of the
                    Australian Information Commissioner, where the law requires us to.
                </p>
                <p>
                    The service does not hold a password for you. Use a strong, unique password for your email account
                    and for each login service, and turn on two-factor authentication where those services offer it.
                </p>
                <p>
                    Be conscious of the information you share online, particularly on public platforms, and maintain the confidentiality of your account details. Should you have any
                    concerns regarding the security of your data, please do not hesitate to contact us immediately.
                </p>
                <a id="capturing-and-printing-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Capturing and Printing Your Personal Data</h3>
                <p>
                    People who can see a page can photograph, screenshot or print it. Once they have that copy, we
                    cannot control what they do with it, and our responsibility for that copy is limited. Think about
                    that before you show a Career profile, or a profile link, to someone else.
                </p>
                <a id="retention-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Retention of Your Personal Data</h3>
                <p>
                    Your personal data will be retained only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your personal data to
                    the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes,
                    and enforce our legal agreements and policies.
                </p>
                <p>
                    Your usage data will also be retained for internal analysis purposes. Usage data is generally retained for a shorter period of time, except when this data is used
                    to strengthen the security or to improve the functionality of the service, or we are legally obligated to retain this data for longer time periods.
                </p>
                <p>
                    Visit records lose what identifies the visitor on a fixed schedule, described under Profile Link Visits.
                </p>
                <a id="deletion-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Deletion of Your Personal Data</h3>
                <p>
                    You have the right to delete or request that we assist in deleting the personal data that we have collected about you.
                </p>
                <p>
                    You may update, amend or delete your information at any time by logging in and using the account or
                    profile pages that let you manage it. There is no control on Xprt Dev or Xprt Jobs that closes the
                    whole account. Email <a href="mailto:team@xprt.dev" target="_blank">team@xprt.dev</a> or
                    <a href="mailto:team@xprt.jobs" target="_blank">team@xprt.jobs</a>, from the address registered to
                    the account, to ask for access to, correction of, or deletion of personal information you cannot
                    change yourself, including closure of the account. A request to either address closes the account on
                    both sites. We may ask for further identification before we close it, so that we do not act on a
                    request from someone else. Closure deletes the account and the data held for it, including your
                    Career profile and My Market. A job application is the exception. Its record, and the resume and
                    cover letter attached to it, stay for the period in the
                    <a href="https://xprt.jobs/legal/privacy" target="_blank">Xprt Jobs Privacy Policy</a>. Closing the
                    account does not shorten that period, and it does not recall a copy an advertiser has already
                    downloaded.
                </p>
                <p>
                    Please note, however, that we may need to retain certain information when we have a legal obligation or lawful basis to do so.
                </p>
                <a id="disclosure-of-your-personal-data" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Disclosure of Your Personal Data</h3>
                <p>
                    We may disclose your personal data where that is necessary to:
                </p>
                <ul>
                    <li>Comply with a legal obligation through court or Government agency.</li>
                    <li>Protect and defend our rights or property.</li>
                    <li>Prevent or investigate possible wrongdoing in connection with the service.</li>
                    <li>Protect the personal safety of users of the service or the public.</li>
                    <li>Protect against legal liability.</li>
                </ul>
                <a id="data-storage-and-international-transfers" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Data Storage and International Transfers</h2>
                <p>
                    Your personal data is primarily stored and processed on servers located in Singapore. We securely
                    transfer encrypted backups for storage in Australia for disaster recovery purposes. Files shown
                    publicly on the service, such as brand logos and Career profile photos, are stored in Australia and
                    may be cached and served from globally distributed edge locations to improve performance.
                </p>
                <p>
                    Depending on your location and how you use the service, your data may be transferred across borders.
                    Google and Sentry process data in the United States, and Atlassian may process a support request
                    outside Australia. We take measures to ensure that your data is treated securely and in accordance
                    with this Privacy Policy, and where required by law we implement appropriate safeguards for
                    international transfers.
                </p>
                <a id="third-party-services" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Third-Party Services</h2>
                <p>
                    We utilise third-party services to enhance the functionality and improve the quality of our service. These third-party services may store data in jurisdictions
                    other than your home country, which is outside of our control.
                </p>
                <a id="google-recaptcha" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Google reCAPTCHA</h3>
                <p>
                    To protect the service from spam and abuse, we use Google reCAPTCHA. This service helps to differentiate between human users and automated bots by analysing their
                    interactions with the service. Google reCAPTCHA may use cookies and collect certain information as part of its analysis, which is processed by Google. For more
                    detailed information on how Google handles your data, please refer to Google's Privacy Policy and Terms of Service.
                </p>
                <ul>
                    <li>Website: <a href="https://www.google.com/recaptcha/about" target="_blank">google.com/recaptcha/about</a></li>
                    <li>Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank">policies.google.com/privacy</a></li>
                    <li>Terms of Service: <a href="https://policies.google.com/terms" target="_blank">policies.google.com/terms</a></li>
                </ul>
                <a id="jira-cloud" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Jira Cloud</h3>
                <p>
                    When you report a bug, request a feature or report a profile, we send your display name and email
                    address, together with what you write and any screenshots you attach, to Jira Cloud. Jira Cloud
                    stores that request so we can track and reply to it. Xprt Dev does not have a separate public
                    contact form. For how Atlassian handles that data, read Atlassian's Privacy Policy and Terms of
                    Service:
                </p>
                <ul>
                    <li>Website: <a href="https://www.atlassian.com" target="_blank">atlassian.com</a></li>
                    <li>Privacy Policy: <a href="https://www.atlassian.com/legal/privacy-policy" target="_blank">atlassian.com/legal/privacy-policy</a></li>
                    <li>Terms of Service: <a href="https://www.atlassian.com/legal/cloud-terms-of-service" target="_blank">atlassian.com/legal/cloud-terms-of-service</a></li>
                </ul>
                <a id="sentry" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Sentry</h3>
                <p>
                    We use Sentry to capture errors and to understand performance. When an error occurs, Sentry may
                    collect your IP address, browser type, and the state of the application at that moment. On a sample
                    of ordinary visits, and whenever an error occurs, Sentry may also record a replay of the session.
                    Text in a replay is masked and media is blocked. Sentry processes this data in the United States,
                    and we use it only to find and fix faults. For how Sentry handles it, read Sentry's Privacy Policy
                    and Terms of Service:
                </p>
                <ul>
                    <li>Website: <a href="https://sentry.io" target="_blank">sentry.io</a></li>
                    <li>Privacy Policy: <a href="https://sentry.io/privacy" target="_blank">sentry.io/privacy</a></li>
                    <li>Terms of Service: <a href="https://sentry.io/terms" target="_blank">sentry.io/terms</a></li>
                </ul>
                <a id="email-delivery" class="no-hover-decoration"></a>
                <h3 class="text-h6 text-weight-bold">Email Delivery</h3>
                <p>
                    We send email through a third-party email delivery provider. It processes the recipient's email
                    address and the content of each message in order to deliver it. The messages are a welcome message,
                    one-time login codes, security notices, and the invitations and profile links you ask us to send. We
                    do not send marketing email.
                </p>
                <a id="minimum-age" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Minimum Age</h2>
                <p>
                    The service is intended for people aged 16 or over, as the
                    <router-link :to="AppRoute.build(Route.TermsOfService)">Terms of Service</router-link> require. We
                    do not knowingly collect personal data from anyone under 16. If you believe we have, please contact
                    us and we will delete it.
                </p>
                <a id="links-to-other-websites" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Links to Other Websites</h2>
                <p>
                    Market pages link to Xprt Jobs, which we operate. Its own terms and privacy policy apply once you
                    are there, as described above.
                </p>
                <p>
                    A Career profile may also link to websites we do not operate, such as a personal site or another
                    public profile. If you follow one of those links, that site's own terms and privacy policy apply. We
                    do not control those sites, and we are not responsible for their content or for what you do there.
                    We will make a reasonable effort to remove or block a harmful link a member has added, once we know
                    about it. We cannot promise we will find every such link first.
                </p>
                <p>
                    Read the privacy policy and terms of any other site before you share personal information there.
                </p>
                <a id="changes-to-this-privacy-policy" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Changes to this Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or as necessary to address new features or services. We
                    encourage you to review this policy periodically to stay informed about how we collect, use and protect your information.
                </p>
                <p>
                    We will notify you of any material changes to this Privacy Policy by posting a prominent notice on
                    the service, or by other means as required by applicable law. Your continued use of the service
                    after the effective date is your acceptance of the updated policy.
                </p>
                <a id="contact-us" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Contact Us</h2>
                <p>
                    If you have a question about this policy or your personal data, email
                    <a href="mailto:team@xprt.dev" target="_blank">team@xprt.dev</a>.
                </p>
                <p>
                    To complain about how we have handled your personal data, or to ask for access to it or a
                    correction, email us with the details. We will look into it and give you a written response within
                    30 days. For an urgent privacy matter, put "Urgent: Privacy Concern" in the subject line.
                </p>
                <p>
                    If you are not satisfied with our response, or we have not responded within 30 days, you can
                    complain to the Office of the Australian Information Commissioner at
                    <a href="https://www.oaic.gov.au" target="_blank">oaic.gov.au</a>.
                </p>
            </article>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.privacy-policy-page {
    .hero-copy {
        max-width: 880px;
    }

    .hero-deck {
        max-width: 720px;
    }
}
</style>
